import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  Stack,
  Text,
  useDisclosure,
  HStack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  IconButton,
  Select,
  Wrap,
  WrapItem,
  Badge
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { CopyIcon } from '@chakra-ui/icons'
import { useToast } from '@chakra-ui/react'
import { getSymbolUsdtPrice } from '../services/apiService'
import { useRecoilValue } from 'recoil'
import { authState } from '../atoms/auth'
import { useTranslation } from 'react-i18next'

const SIZE_UNIT_MAP: Record<string, number> = {
  BTC: 0.0001,
  ETH: 0.01,
  XRP: 10,
  DOGE: 10,
  SOL: 1
}
const getSizeUnit = (symbol: string) =>
  SIZE_UNIT_MAP[symbol?.toUpperCase()] ?? 1

const SizeCalculator = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const auth = useRecoilValue(authState)

  const { isOpen, onOpen, onClose } = useDisclosure()

  // 심볼 리스트: auth.trading 배열 기반
  const availableSymbols = useMemo(
    () =>
      Array.isArray(auth?.trading) ? auth.trading.map((t) => t.symbol) : [],
    [auth?.trading]
  )
  const defaultSymbol = useMemo(() => {
    if (!availableSymbols.length) return 'XRP'
    return availableSymbols.includes('XRP') ? 'XRP' : availableSymbols[0]
  }, [availableSymbols])

  const [symbol, setSymbol] = useState<string>(defaultSymbol)

  useEffect(() => {
    if (!availableSymbols.includes(symbol)) {
      setSymbol(defaultSymbol)
    }
  }, [availableSymbols, defaultSymbol, symbol])

  const [seed, setSeed] = useState('1000')
  const [leverage, setLeverage] = useState('30')
  const [price, setPrice] = useState('0.0000') // 최신 심볼 가격
  const [divide, setDivide] = useState('100') // 나눔 횟수(회차 수)

  const parsedSeed = parseFloat(seed) || 0
  const parsedLeverage = parseFloat(leverage) || 0
  const parsedPrice = parseFloat(price) || 0
  const parsedDivide = parseFloat(divide) || 1

  // 회차당 실제 진입 금액
  const entryAmount = parsedDivide > 0 ? parsedSeed / parsedDivide : 0
  // 레버리지 적용 금액
  const leveragedAmount = entryAmount * parsedLeverage
  // 코인 수량
  const entryCoinAmount = parsedPrice > 0 ? leveragedAmount / parsedPrice : 0

  // 심볼별 size 단위
  const sizeUnit = getSizeUnit(symbol)
  // 자동매매용 size = floor(코인수량 / 단위)
  const entrySizeUnits = Math.floor(entryCoinAmount / sizeUnit)

  const handleCopySize = () => {
    navigator.clipboard.writeText(String(entrySizeUnits))
    toast({
      title: t('사이즈 복사 완료'),
      description: t('"{{value}}" 이 클립보드에 복사되었습니다.', {
        value: entrySizeUnits
      }),
      status: 'success',
      duration: 2000,
      isClosable: true
    })
  }

  // 가격 조회
  useEffect(() => {
    const fetchPrice = async () => {
      if (!symbol) return
      try {
        const { data: res } = await getSymbolUsdtPrice(symbol)
        if (res.statusCode === 200) {
          const fetchedPrice = parseFloat(res.data)
          setPrice(isFinite(fetchedPrice) ? fetchedPrice.toFixed(4) : '0.0000')
        } else {
          toast({
            title: t('가격 조회 실패'),
            description: t('{{symbol}} 가격을 불러오지 못했습니다.', {
              symbol
            }),
            status: 'error',
            duration: 3000,
            isClosable: true
          })
        }
      } catch (error) {
        toast({
          title: t('오류'),
          description: t('{{symbol}} 가격 조회 중 오류가 발생했습니다.', {
            symbol
          }),
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    }

    fetchPrice()
  }, [symbol, isOpen]) // 모달 열릴 때/심볼 변경 시 갱신

  return (
    <>
      <Button variant="outline" colorScheme="purple" onClick={onOpen} w="100%">
        {t('수량계산기 (선물)')}
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('수량계산기')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              {/* 심볼 선택 */}
              <Stack>
                <Text fontWeight="medium">{t('심볼 선택')}</Text>
                {/* 빠른 선택 버튼 */}
                <Wrap mb={2}>
                  {(availableSymbols.length
                    ? availableSymbols
                    : ['BTC', 'ETH', 'XRP', 'DOGE', 'SOL']
                  ).map((sym) => (
                    <WrapItem key={sym}>
                      <Button
                        size="xs"
                        variant={symbol === sym ? 'solid' : 'outline'}
                        colorScheme={symbol === sym ? 'purple' : 'gray'}
                        onClick={() => setSymbol(sym)}
                      >
                        {sym}
                      </Button>
                    </WrapItem>
                  ))}
                </Wrap>
                {/* 셀렉트 */}
                <HStack>
                  <Select
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value)}
                    maxW="200px"
                  >
                    {(availableSymbols.length
                      ? availableSymbols
                      : ['BTC', 'ETH', 'XRP', 'DOGE', 'SOL']
                    ).map((sym) => (
                      <option key={sym} value={sym}>
                        {sym}
                      </option>
                    ))}
                  </Select>
                  <Badge colorScheme="purple" variant="subtle">
                    {t('1 size = {{sizeUnit}} {{symbol}}', {
                      sizeUnit,
                      symbol
                    })}
                  </Badge>
                </HStack>
              </Stack>

              {/* 현재 가격 */}
              <Stack>
                <Text fontWeight="medium">
                  {t('현재 {{symbol}} 가격 (USDT)', { symbol })}
                </Text>
                <Input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  readOnly
                  bg="gray.200"
                />
              </Stack>

              {/* 전체 시드 */}
              <Stack>
                <Text fontWeight="medium">{t('전체 시드 (USDT)')}</Text>
                {/* 퀵 버튼: 현재 입력값 기준 % 변환 */}
                <HStack flexWrap="wrap">
                  {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((percent) => (
                    <Button
                      key={percent}
                      size="xs"
                      variant="outline"
                      onClick={() => {
                        const total = parseFloat(seed)
                        if (!isNaN(total)) {
                          const calculated = Math.floor((total * percent) / 100)
                          setSeed(String(calculated))
                        }
                      }}
                    >
                      {percent}%
                    </Button>
                  ))}
                </HStack>
                <Input
                  type="number"
                  value={seed}
                  onChange={(e) => setSeed(e.target.value)}
                />
              </Stack>

              {/* 레버리지 */}
              <Stack>
                <Text fontWeight="medium">
                  {t('레버리지: {{leverage}}배', { leverage })}
                </Text>

                {/* 퀵 버튼 */}
                <HStack flexWrap="wrap">
                  {[1, 5, 10, 20, 30, 40, 50, 60, 75].map((val) => (
                    <Button
                      key={val}
                      size="xs"
                      colorScheme={leverage === String(val) ? 'purple' : 'gray'}
                      variant={leverage === String(val) ? 'solid' : 'outline'}
                      onClick={() => setLeverage(String(val))}
                    >
                      {val}x
                    </Button>
                  ))}
                </HStack>

                <HStack>
                  <Slider
                    value={Number(leverage)}
                    onChange={(val) => setLeverage(String(val))}
                    min={1}
                    max={75}
                    step={1}
                    flex="1"
                  >
                    <SliderTrack>
                      <SliderFilledTrack bg="purple.500" />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>

                  <Input
                    w="70px"
                    type="number"
                    min={1}
                    max={75}
                    value={leverage}
                    onChange={(e) => setLeverage(e.target.value)}
                    onBlur={() => {
                      if (!leverage || Number(leverage) < 1) setLeverage('1')
                    }}
                  />
                </HStack>
              </Stack>

              {/* 나눔 횟수 */}
              <Stack>
                <Text fontWeight="medium">{t('나눔 횟수')}</Text>

                {/* 퀵 버튼 */}
                <HStack flexWrap="wrap">
                  {[3, 5, 10, 15, 20, 25, 30, 50, 100, 200, 300].map((val) => (
                    <Button
                      key={val}
                      size="xs"
                      variant="outline"
                      onClick={() => setDivide(String(val))}
                    >
                      {val}
                    </Button>
                  ))}
                </HStack>

                <Input
                  type="number"
                  value={divide}
                  onChange={(e) => setDivide(e.target.value)}
                />
              </Stack>
            </Stack>

            <Stack
              mt={6}
              spacing={4}
              p={4}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              bg="gray.50"
            >
              <Text fontWeight="bold" fontSize="lg">
                {t('계산 결과')}
              </Text>

              <Stack spacing={1}>
                <HStack justify="space-between">
                  <Text color="gray.600">{t('📌 1회 실제 진입 금액')}</Text>
                  <Text fontWeight="semibold">${entryAmount.toFixed(2)}</Text>
                </HStack>

                <HStack justify="space-between">
                  <Text color="gray.600">{t('📌 1회 진입 수량')}</Text>
                  <Text fontWeight="semibold">
                    {Math.floor(entryCoinAmount)} {symbol}
                  </Text>
                </HStack>

                <HStack justify="space-between" align="center">
                  <Text color="gray.600">
                    {t('📌 자동매매용 사이즈 값')}{' '}
                    <Text as="span" color="gray.500" fontSize="sm">
                      (
                      {t('1 size = {{sizeUnit}} {{symbol}}', {
                        sizeUnit,
                        symbol
                      })}
                      )
                    </Text>
                  </Text>
                  <HStack>
                    <Text fontWeight="semibold" color="purple.600">
                      {entrySizeUnits} size
                    </Text>
                    <IconButton
                      aria-label="사이즈 복사"
                      size="xs"
                      icon={<CopyIcon />}
                      onClick={handleCopySize}
                      variant="ghost"
                      colorScheme="purple"
                    />
                  </HStack>
                </HStack>
              </Stack>

              <Text fontSize="sm" color="gray.500">
                {t('사이즈 값 안내')}
              </Text>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>{t('닫기')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default SizeCalculator
